import React, { useState, useEffect, useRef } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { 
  getBrainDumps, 
  saveBrainDump, 
  updateBrainDump, 
  deleteBrainDump 
} from '../utils/localStorage';
import { 
  Brain, 
  Plus, 
  Trash2, 
  Clock,
  Home,
  ShoppingCart,
  Heart,
  Calendar,
  Lightbulb,
  PenLine,
  Mic,
  MicOff,
  Loader2,
  Square
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { id: 'household', label: 'Household', icon: Home, color: 'bg-sage/20 text-sage-dark border-sage/30' },
  { id: 'errands', label: 'Errands', icon: ShoppingCart, color: 'bg-brown/20 text-brown-dark border-brown/30' },
  { id: 'family', label: 'Family', icon: Heart, color: 'bg-warmth-clay/20 text-warmth-rust border-warmth-clay/30' },
  { id: 'appointments', label: 'Appointments', icon: Calendar, color: 'bg-normal-bg text-normal-text border-normal-border' },
  { id: 'ideas', label: 'Ideas', icon: Lightbulb, color: 'bg-good-bg text-good-text border-good-border' },
];

const JournalEntry = ({ item, onUpdate, onDelete }) => {
  const category = categories.find(c => c.id === item.category) || categories[0];
  const CategoryIcon = category.icon;
  const dateObj = new Date(item.createdAt);
  const formattedDate = dateObj.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
  
  return (
    <div 
      data-testid={`brain-dump-item-${item.id}`}
      className={`
        journal-entry p-4 transition-all duration-300 animate-float-in relative
        ${item.completed ? 'opacity-60' : ''}
      `}
    >
      {/* Washi tape decoration */}
      <div className="absolute -top-2 left-8 w-16 h-5 bg-sage/40 transform -rotate-2 rounded-sm" />
      
      <div className="flex items-start gap-3 pt-2">
        <Checkbox
          data-testid={`brain-dump-checkbox-${item.id}`}
          checked={item.completed}
          onCheckedChange={(checked) => onUpdate(item.id, { completed: checked })}
          className="mt-1 border-brown"
        />
        
        <div className="flex-1 min-w-0">
          {/* Handwritten-style text */}
          <p className={`
            font-handwritten text-xl text-ink-900 leading-relaxed
            ${item.completed ? 'line-through text-ink-500' : ''}
          `}>
            {item.text}
          </p>
          
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <Badge className={`${category.color} text-xs border`}>
              <CategoryIcon className="w-3 h-3 mr-1" />
              {category.label}
            </Badge>
            <span className="text-xs text-ink-500 flex items-center gap-1 font-accent italic">
              <Clock className="w-3 h-3" />
              {formattedDate}
            </span>
            {item.isVoice && (
              <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-xs border">
                <Mic className="w-3 h-3 mr-1" />
                Voice
              </Badge>
            )}
          </div>
        </div>
        
        <Button
          data-testid={`brain-dump-delete-${item.id}`}
          onClick={() => onDelete(item.id)}
          variant="ghost"
          size="icon"
          className="text-ink-500 hover:text-destructive h-8 w-8"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// Voice Recorder Component
const VoiceRecorder = ({ onTranscription, onCancel }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Use webm format which is widely supported
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') 
        ? 'audio/webm' 
        : 'audio/mp4';
      
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: mimeType });
        stream.getTracks().forEach(track => track.stop());
        await transcribeAudio(audioBlob);
      };
      
      mediaRecorderRef.current.start(1000); // Collect data every second
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Microphone access error:', error);
      toast.error('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);
    
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');
      
      const response = await fetch(`${API_URL}/api/voice/transcribe`, {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error('Transcription failed');
      }
      
      const data = await response.json();
      
      if (data.success && data.text) {
        onTranscription(data.text);
        toast.success('Voice transcribed successfully!');
      } else {
        throw new Error('No transcription received');
      }
    } catch (error) {
      console.error('Transcription error:', error);
      toast.error('Could not transcribe audio. Please try again or type instead.');
      onCancel();
    } finally {
      setIsTranscribing(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  if (isTranscribing) {
    return (
      <Card className="p-6 bg-purple-50 border-purple-200 text-center animate-fade-in">
        <Loader2 className="w-8 h-8 mx-auto mb-3 text-purple-600 animate-spin" />
        <p className="text-purple-700 font-accent">Transcribing your thoughts...</p>
        <p className="text-purple-500 text-sm mt-1">This may take a moment</p>
      </Card>
    );
  }

  return (
    <Card className="p-6 bg-paper-light shadow-lifted border border-stone-200 animate-fade-in">
      <div className="text-center">
        {!isRecording ? (
          <>
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center border-2 border-purple-300">
              <Mic className="w-8 h-8 text-purple-600" />
            </div>
            <h3 className="font-serif text-lg text-ink-900 mb-2">Ready to listen</h3>
            <p className="text-ink-500 text-sm font-accent italic mb-4">
              Tap to start recording your thoughts
            </p>
            <div className="flex gap-2 justify-center">
              <Button
                data-testid="voice-start-btn"
                onClick={startRecording}
                className="bg-purple-600 hover:bg-purple-700 text-white rounded-full px-6"
              >
                <Mic className="w-4 h-4 mr-2" />
                Start Recording
              </Button>
              <Button
                onClick={onCancel}
                variant="outline"
                className="rounded-full border-stone-300"
              >
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-red-100 to-red-200 flex items-center justify-center border-2 border-red-300 animate-pulse">
              <div className="w-4 h-4 bg-red-500 rounded-full animate-pulse" />
            </div>
            <h3 className="font-serif text-lg text-ink-900 mb-1">Recording...</h3>
            <p className="text-2xl font-mono text-red-600 mb-4">{formatTime(recordingTime)}</p>
            <p className="text-ink-500 text-sm font-accent italic mb-4">
              Speak naturally. Tap stop when you're done.
            </p>
            <Button
              data-testid="voice-stop-btn"
              onClick={stopRecording}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full px-6"
            >
              <Square className="w-4 h-4 mr-2" />
              Stop Recording
            </Button>
          </>
        )}
      </div>
    </Card>
  );
};

const BrainDump = () => {
  const [dumps, setDumps] = useState([]);
  const [newDump, setNewDump] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('household');
  const [showInput, setShowInput] = useState(false);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [filter, setFilter] = useState('all');
  
  useEffect(() => {
    setDumps(getBrainDumps());
  }, []);
  
  const handleAdd = (isVoice = false) => {
    if (!newDump.trim()) return;
    
    const dump = saveBrainDump({
      text: newDump.trim(),
      category: selectedCategory,
      isVoice: isVoice,
    });
    
    setDumps([dump, ...dumps]);
    setNewDump('');
    setShowInput(false);
    setShowVoiceRecorder(false);
    toast.success('Added to your journal');
  };

  const handleVoiceTranscription = (text) => {
    setNewDump(text);
    setShowVoiceRecorder(false);
    setShowInput(true);
  };
  
  const handleUpdate = (id, updates) => {
    const updated = updateBrainDump(id, updates);
    setDumps(updated);
  };
  
  const handleDelete = (id) => {
    const updated = deleteBrainDump(id);
    setDumps(updated);
    toast.success('Removed from your journal');
  };
  
  const filteredDumps = dumps.filter(d => {
    if (filter === 'all') return true;
    if (filter === 'active') return !d.completed;
    if (filter === 'completed') return d.completed;
    return d.category === filter;
  });
  
  const activeDumps = dumps.filter(d => !d.completed);
  
  return (
    <div className="space-y-6 relative" data-testid="brain-dump-page">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 text-4xl opacity-20 pointer-events-none">🌿</div>
      
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-serif text-2xl md:text-3xl text-ink-900 mb-2">
            Mental Offloader
          </h2>
          <p className="text-ink-500 font-accent italic">
            {activeDumps.length} things on your mind • Write or say them here
          </p>
        </div>
        <div className="w-12 h-12 rounded-full bg-brown/10 flex items-center justify-center border border-brown/20">
          <Brain className="w-6 h-6 text-brown" />
        </div>
      </div>
      
      {/* Notebook header decoration */}
      <div className="flex items-center gap-2 text-ink-300">
        <div className="flex-1 h-px bg-stone-300" />
        <span className="text-xs font-accent">~ your private journal ~</span>
        <div className="flex-1 h-px bg-stone-300" />
      </div>
      
      {/* Voice Recorder */}
      {showVoiceRecorder && (
        <VoiceRecorder 
          onTranscription={handleVoiceTranscription}
          onCancel={() => setShowVoiceRecorder(false)}
        />
      )}
      
      {/* Add new entry */}
      {showInput && !showVoiceRecorder ? (
        <Card className="p-5 bg-paper-light shadow-lifted border border-stone-200 animate-fade-in journal-lines relative">
          {/* Decorative washi tape */}
          <div className="absolute -top-2 right-12 w-20 h-5 bg-warmth-clay/40 transform rotate-1 rounded-sm" />
          
          <div className="flex items-center gap-2 mb-3">
            <PenLine className="w-4 h-4 text-brown" />
            <span className="font-serif text-ink-900">New thought...</span>
          </div>
          
          <Textarea
            data-testid="brain-dump-input"
            value={newDump}
            onChange={(e) => setNewDump(e.target.value)}
            placeholder="What's weighing on your mind? Let it out here..."
            className="mb-4 min-h-[120px] border-stone-300 focus:border-brown focus:ring-brown/20 font-handwritten text-lg bg-transparent"
            autoFocus
          />
          
          {/* Category selector */}
          <div className="flex flex-wrap gap-2 mb-4">
            {categories.map((cat) => {
              const Icon = cat.icon;
              return (
                <Button
                  key={cat.id}
                  data-testid={`brain-dump-category-${cat.id}`}
                  onClick={() => setSelectedCategory(cat.id)}
                  variant={selectedCategory === cat.id ? "default" : "outline"}
                  size="sm"
                  className={`
                    rounded-full text-xs
                    ${selectedCategory === cat.id ? 'bg-brown text-white' : 'border-stone-300'}
                  `}
                >
                  <Icon className="w-3 h-3 mr-1" />
                  {cat.label}
                </Button>
              );
            })}
          </div>
          
          <div className="flex gap-2">
            <Button
              data-testid="brain-dump-save"
              onClick={() => handleAdd(false)}
              disabled={!newDump.trim()}
              className="vintage-btn btn-brown rounded-lg"
            >
              Save to journal
            </Button>
            <Button
              data-testid="brain-dump-cancel"
              onClick={() => {
                setShowInput(false);
                setNewDump('');
              }}
              variant="ghost"
              className="rounded-lg text-ink-500"
            >
              Cancel
            </Button>
          </div>
        </Card>
      ) : !showVoiceRecorder && (
        <div className="grid grid-cols-2 gap-3">
          <Button
            data-testid="brain-dump-add-btn"
            onClick={() => setShowInput(true)}
            className="py-6 rounded-xl bg-paper-light border-2 border-dashed border-stone-300 hover:border-brown text-ink-500 hover:text-brown transition-all"
            variant="ghost"
          >
            <Plus className="w-5 h-5 mr-2" />
            Write it down
          </Button>
          <Button
            data-testid="brain-dump-voice-btn"
            onClick={() => setShowVoiceRecorder(true)}
            className="py-6 rounded-xl bg-purple-50 border-2 border-dashed border-purple-200 hover:border-purple-400 text-purple-600 hover:text-purple-700 transition-all"
            variant="ghost"
          >
            <Mic className="w-5 h-5 mr-2" />
            Say it loud
          </Button>
        </div>
      )}
      
      {/* Filters */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {['all', 'active', 'completed', ...categories.map(c => c.id)].map((f) => {
          const cat = categories.find(c => c.id === f);
          const label = f === 'all' ? 'All' : f === 'active' ? 'Active' : f === 'completed' ? 'Done' : cat?.label;
          
          return (
            <Button
              key={f}
              data-testid={`brain-dump-filter-${f}`}
              onClick={() => setFilter(f)}
              variant={filter === f ? "default" : "outline"}
              size="sm"
              className={`
                rounded-full text-xs whitespace-nowrap
                ${filter === f ? 'bg-brown text-white' : 'border-stone-300'}
              `}
            >
              {label}
            </Button>
          );
        })}
      </div>
      
      {/* Journal entries */}
      <div className="space-y-4">
        {filteredDumps.length === 0 ? (
          <Card className="p-8 text-center bg-paper-light shadow-lifted border border-stone-200">
            <div className="text-4xl mb-4 opacity-50">📓</div>
            <p className="text-ink-500 font-accent italic">
              {filter === 'all' 
                ? "Your journal is empty. That's peaceful." 
                : `No ${filter} entries right now.`
              }
            </p>
          </Card>
        ) : (
          filteredDumps.map((dump, idx) => (
            <JournalEntry
              key={dump.id}
              item={dump}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              style={{ animationDelay: `${idx * 0.1}s` }}
            />
          ))
        )}
      </div>
      
      {/* Gentle reminder */}
      {activeDumps.length > 5 && (
        <Card className="p-4 bg-warmth-sand border border-stone-300 text-center">
          <p className="text-sm text-ink-700 font-handwritten text-lg">
            🌿 You're holding a lot. Remember: not everything needs to happen today.
          </p>
        </Card>
      )}
    </div>
  );
};

export default BrainDump;
